import { useQuery, useMutation } from "@tanstack/react-query";
import queryString from "query-string";

import { api } from "@services/index";
import {
  ApiErrorModel,
  MileageFormInterface,
  MileageFormInterfaceDto,
  MileageModel,
  MileageModelDto,
  PaginationModel,
  PaginationModelDto,
} from "@interfaces/index";
import { formatDateForApi } from "@utils/index";

const prepareMileageToModel = (mileage: MileageModelDto): MileageModel => ({
  uniqueId: mileage.unique_id,
  dateFrom: mileage.date_from,
  dateTo: mileage.date_to,
  userBusiness: {
    uniqueId: mileage.user_business?.unique_id,
    name: mileage.user_business?.name,
    type: mileage.user_business?.type,
    tradingStartDate: mileage.user_business?.trading_start_date,
  },
  asset: {
    uniqueId: mileage.asset?.unique_id,
    name: mileage.asset?.name,
    type: mileage.asset?.type,
    purchaseType: mileage.asset?.purchase_type,
    date: mileage.asset?.date,
    firstLetAt: mileage.asset?.first_let_at,
    cost: mileage.asset?.cost,
    jointShare: mileage.asset?.joint_share,
    disposal: mileage.asset?.disposal,
    policies: mileage.asset?.policies,
  },
  period: {
    title: mileage.period?.title,
    status: {
      value: mileage.period?.status.value,
      label: mileage.period?.status.label,
    },
    startDate: mileage.period?.start_date,
    endDate: mileage.period?.end_date,
  },
  comments: mileage.comments,
  businessMiles: mileage.business_miles,
  privateMiles: mileage.private_miles,
  totalMiles: mileage.total_miles,
  mileageSum: mileage.mileage_sum,
  privateUse: mileage.private_use,
  policies: {
    canDelete: mileage.policies.canDelete,
    canUpdate: mileage.policies.canUpdate,
  },
});

export const useMileageGetAll = ({
  businessId = "",
  assetId = "",
  startDate = "",
  endDate = "",
  page,
}: {
  businessId?: string;
  assetId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
}) => {
  const queryFn = async () => {
    const params = queryString.stringify(
      {
        user_business_unique_id: businessId,
        asset_unique_id: assetId,
        start_date: startDate ? `${startDate}T00:00:00` : "",
        end_date: endDate ? `${endDate}T23:59:59` : "",
        page,
      },
      { skipEmptyString: true },
    );
    const { data: dataDto } = await api.get<{
      data: MileageModelDto[];
      meta: PaginationModelDto;
    }>(`/mileage-log?${params}`);

    const mileages = dataDto.data.map((mileage) =>
      prepareMileageToModel(mileage),
    );
    const pagination: PaginationModel = {
      total: dataDto.meta.total,
      perPage: dataDto.meta.per_page,
    };

    return {
      mileages,
      pagination,
    };
  };

  return useQuery<
    {
      mileages: MileageModel[];
      pagination: PaginationModel;
    },
    ApiErrorModel
  >({
    queryKey: ["mileageGetAll", businessId, assetId, startDate, endDate, page],
    queryFn,
    refetchOnWindowFocus: true,
    enabled: !!businessId,
  });
};

export const useMileageGetOne = (id: string) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<MileageModelDto>(
      `/mileage-log/${id}`,
    );

    return prepareMileageToModel(dataDto);
  };

  return useQuery<MileageModel, ApiErrorModel>({
    queryKey: ["mileageGetOne", id],
    queryFn,
    enabled: !!id,
  });
};

export const useMileageCreate = (
  onSuccess?: (pressedCreateNewBtn: boolean, previousDateTo: Date) => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    businessId,
    assetId,
    dateFrom,
    dateTo,
    comments,
    businessMiles,
    privateMiles,
  }: MileageFormInterface) => {
    const fieldsDto: MileageFormInterfaceDto = {
      user_business_unique_id: businessId,
      asset_unique_id: assetId,
      date_from: formatDateForApi(dateFrom, true),
      date_to: formatDateForApi(dateTo, true),
      comments,
      business_miles: businessMiles,
      private_miles: privateMiles,
    };
    await api.post("/mileage-log", fieldsDto);
  };

  return useMutation({
    mutationKey: ["mileageCreate"],
    mutationFn,
    onSuccess: (_, { pressedCreateNewBtn, dateTo }) => {
      if (onSuccess) {
        onSuccess(!!pressedCreateNewBtn, dateTo);
      }
    },
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        businessId: error.fields?.user_business_unique_id || "",
        assetId: error.fields?.asset_unique_id || "",
        dateFrom: error.fields?.date_from || "",
        dateTo: error.fields?.date_to || "",
        comments: error.fields?.comments || "",
        businessMiles: error.fields?.business_miles || "",
        privateMiles: error.fields?.private_miles || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useMileageUpdate = (
  onSuccess?: (pressedCreateNewBtn: boolean, previousDateTo: Date) => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    mileageId,
    businessId,
    assetId,
    dateFrom,
    dateTo,
    comments,
    businessMiles,
    privateMiles,
  }: MileageFormInterface) => {
    const fieldsDto: MileageFormInterfaceDto = {
      user_business_unique_id: businessId,
      asset_unique_id: assetId,
      date_from: formatDateForApi(dateFrom, true),
      date_to: formatDateForApi(dateTo, true),
      comments,
      business_miles: businessMiles,
      private_miles: privateMiles,
    };
    await api.put(`/mileage-log/${mileageId}`, fieldsDto);
  };

  return useMutation({
    mutationKey: ["mileageUpdate"],
    mutationFn,
    onSuccess: (_, { pressedCreateNewBtn, dateTo }) => {
      if (onSuccess) {
        onSuccess(!!pressedCreateNewBtn, dateTo);
      }
    },
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        businessId: error.fields?.user_business_unique_id || "",
        assetId: error.fields?.asset_unique_id || "",
        dateFrom: error.fields?.date_from || "",
        dateTo: error.fields?.date_to || "",
        comments: error.fields?.comments || "",
        businessMiles: error.fields?.business_miles || "",
        privateMiles: error.fields?.private_miles || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useMileageDelete = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async (id: string) =>
    await api.delete(`/mileage-log/${id}`);

  return useMutation({
    mutationKey: ["mileageDelete"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};
